import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Database, ExternalLink } from 'lucide-react';
import { CatalogImageModal } from './CatalogImageModal';
import { useDragScroll } from '../useDragScroll';
import { displayVariantLabel } from '../variantLabel';
import { DimensionBadge } from '../DimensionBadge';
import { useLanguage } from '../../../contexts/LanguageContext';
import { pictureSrc } from '../pictureRef';

// The bytes of one stored product photo, by its id.
export const listingImageSrc = (id: number) => pictureSrc({ source: 'listing', imageId: id });

// One stored product photo: its id, the ID of the submodel (colour/material variant) it
// came from, and what that submodel is called. `variant` is '' when the notice has a
// single listing, or the photo predates submodels; `variantLabel` is display only — the
// grouping is always by id, which survives a re-read of the listing.
export type ProductPhoto = {
  id: number;
  variant?: string;
  variantLabel?: string;
  // Whether this picture carries measurements drawn on it. It is where a part's real
  // size comes from, so it is worth seeing in the database and not only in the strip a
  // listing read happened to produce — the Parts DB outlives any one reading.
  dimensioned?: boolean;
};

// One notice and the finished-product photos harvested from its listing gallery.
export type NoticePhotos = {
  docId: string;
  docName: string | null;
  slug: string | null;
  images: ProductPhoto[];
};

// A notice's photos split into submodel groups, in first-appearance order — the backend
// returns them contiguously by position, so a colour's photos stay together. A notice
// with one listing yields a single group whose variant is '' (no caption, one row).
//
// Grouped by the submodel ID; `label` is what that group is captioned with, taken from
// the first photo of the group that carries one (they are stamped together on save, and
// a photo saved before the id/label split carries none).
type PhotoGroup = { variant: string; label: string; images: ProductPhoto[] };

function groupByVariant(images: ProductPhoto[]): PhotoGroup[] {
  const groups: PhotoGroup[] = [];
  const byVariant = new Map<string, PhotoGroup>();
  for (const im of images) {
    const v = im.variant || '';
    let g = byVariant.get(v);
    if (!g) {
      g = { variant: v, label: '', images: [] };
      byVariant.set(v, g);
      groups.push(g);
    }
    if (!g.label) g.label = (im.variantLabel || '').trim();
    g.images.push(im);
  }
  return groups;
}

// The Product photos section of the Parts DB & Photos tab: every notice's finished-
// product photos (saved from its web listing(s) in Phase 1). One TABLE PER NOTICE, titled
// with it, and inside it one row per submodel colour, each a sliding strip of thumbnails.
// Controlled — the parent owns the data (so it can share the notice list with the parts
// filter) and applies the notice filter before passing it here; a delete calls back up.
export function NoticeProductImages({
  notices,
  onDeleted,
}: {
  notices: NoticePhotos[];
  onDeleted: (id: number) => void;
}) {
  const { t } = useLanguage();
  const [zoom, setZoom] = useState<number | null>(null);

  const cellCls = 'border border-slate-300 px-4 py-2 text-center align-middle';

  const remove = (id: number) => {
    setZoom(null);
    onDeleted(id);
  };

  // What ← and → walk from the zoomed photo: its OWN notice's photos, in the order this
  // table lays them out (colour row by colour row). Not the whole page — the zoom names no
  // manual, so arrowing out of one notice and into the next would say nothing about having
  // left it.
  const stripAround = (id: number) => {
    const n = notices.find((x) => x.images.some((im) => im.id === id));
    return n ? groupByVariant(n.images).flatMap((g) => g.images.map((im) => im.id)) : [];
  };

  // Photos the user has switched OFF (grey) but not yet committed: they stay on screen,
  // greyed, and are removed from the database only when the user leaves this view — so a
  // toggle can be undone, and a whole colour curated, before anything is actually deleted.
  const [pendingRemoval, setPendingRemoval] = useState<Set<number>>(new Set());
  // The unmount cleanup fires the deferred deletes; a ref carries the latest set to it,
  // since that effect runs once (its closure would otherwise hold the first, empty set).
  const pendingRef = useRef(pendingRemoval);
  pendingRef.current = pendingRemoval;
  useEffect(
    () => () => {
      for (const id of pendingRef.current) {
        void axios.delete(`/api/notice/listing-image/${id}`).catch(() => {});
      }
    },
    [],
  );

  // Nothing to show (none saved, or none for the selected notice) — omit the section.
  // Below the hooks, not above them: the filter can empty this list and fill it again,
  // and a return that skipped half the hooks would take React's state with it.
  if (notices.length === 0) return null;

  const togglePending = (id: number) =>
    setPendingRemoval((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    // No top margin: this is the top of its own tab now, not a block stacked under the
    // parts table it used to need clearing from.
    <div>
      <div className="mb-2 flex items-center justify-center">
        <h2 className="text-lg font-bold text-slate-900">
          {t('notice.catalog.productPhotos')}
        </h2>
      </div>
      <div className="h-8" />

      {/* A table per notice, titled with the notice. The name used to be a left column
          repeating itself down every colour row of the same manual; as a title it is said
          once, over the photos it belongs to, and the table under it is left to be what it
          is — that notice's colours. No header row: one column, and the only thing it
          could be called is "Photos produit", which the heading above already says. */}
      {notices.map((n) => (
        <div key={n.docId} className="mb-6 last:mb-0">
          <NoticeTitle notice={n} t={t} />
          {/* Same card as the parts table next door, and for the same reason: the cells'
              ink is chosen for a light surface and the table brought none. The table's own
              outer border goes with it — the cells draw theirs (cellCls), so the card's
              edge and the table's were two lines round one rectangle. */}
          <div className="overflow-x-auto rounded-xl border-2 border-slate-300 bg-white shadow-sm">
            <table className="w-full border-collapse text-sm">
              <tbody>
                {groupByVariant(n.images).map((g, i) => (
                  <tr key={g.variant || i}>
                    <PhotoRow
                      group={g}
                      cellCls={cellCls}
                      onZoom={setZoom}
                      pendingRemoval={pendingRemoval}
                      onTogglePending={togglePending}
                    />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {zoom != null && (
        <CatalogImageModal
          src={listingImageSrc(zoom)}
          id={zoom}
          siblings={stripAround(zoom)}
          onNavigate={setZoom}
          deleteUrl={`/api/notice/listing-image/${zoom}`}
          deleteLabel={t('notice.catalog.deletePhoto')}
          onDeleted={() => remove(zoom)}
          onClose={() => setZoom(null)}
        />
      )}
    </div>
  );
}

// The title over one notice's table: its /view/<slug> identity (brand-category-item),
// a link into the manual when it still exists, plain text otherwise — photos outlive the
// manual they were harvested from, and "source unknown" is the honest thing to say.
//
// Same green monospace slug the old Notice column used, because it is the same fact; it
// is only said once now, and centered over what it names.
function NoticeTitle({ notice, t }: { notice: NoticePhotos; t: (k: string) => string }) {
  const label = notice.slug || notice.docName;
  return (
    <div className="mb-2 flex justify-center">
      {label ? (
        <Link
          to={`/view/${notice.slug || notice.docId}`}
          title={notice.docName || label}
          className="inline-flex max-w-full items-center gap-1 font-mono text-sm font-semibold text-blue-600 underline decoration-blue-600/40 underline-offset-4 transition-colors hover:decoration-blue-600"
        >
          <span className="truncate">{label}</span>
          <ExternalLink className="h-3.5 w-3.5 shrink-0" />
        </Link>
      ) : (
        <span className="font-mono text-sm italic text-slate-400">
          {t('notice.catalog.schematicUnknownSource')}
        </span>
      )}
    </div>
  );
}

// One submodel's photos as a sliding strip, captioned with its colour label (omitted for
// an unlabelled group). Each thumbnail opens zoomed, and carries the same "in the
// database" switch as the Phase 1 gallery beneath it — filled, because every photo here IS
// in the database; clicking it removes that photo from the set.
function PhotoRow({
  group,
  cellCls,
  onZoom,
  pendingRemoval,
  onTogglePending,
}: {
  group: PhotoGroup;
  cellCls: string;
  onZoom: (id: number) => void;
  pendingRemoval: Set<number>;
  onTogglePending: (id: number) => void;
}) {
  const ref = useDragScroll<HTMLDivElement>();
  return (
    <td className={cellCls}>
      {/* The caption is the stored label, through displayVariantLabel like everywhere a
          variant is shown — a row written by an older build holds the raw grouping key,
          and this is what turns it into a sentence. A group saved before the id/label
          split has no label at all, and its id IS that old key, so it reads the same. */}
      {group.variant && (
        <div className="mb-1.5 text-xs font-semibold text-slate-500">
          {displayVariantLabel(group.label || group.variant)}
          {/* HOW MANY PICTURES THIS COLOUR HAS. The strip scrolls, so its own length is
              the one thing you cannot see by looking at it — three visible could be three
              or thirty. Lighter than the label: it says something ABOUT the group rather
              than naming it. */}
          <span className="ml-1 font-normal tabular-nums opacity-70">
            ({group.images.length})
          </span>
        </div>
      )}
      {/* w-fit, not justify-center: centered while the photos fit, but once they overflow
          it scrolls from the START (justify-center hides the first photo's left edge). */}
      <div ref={ref} className="scrollbar-hide mx-auto flex w-fit max-w-[32rem] cursor-grab gap-1.5 overflow-x-auto">
        {group.images.map((im) => (
          <div key={im.id} className="flex shrink-0 flex-col items-center gap-1.5">
            <button
              type="button"
              onClick={() => onZoom(im.id)}
              className="relative rounded transition-opacity hover:opacity-80"
            >
              <img
                src={listingImageSrc(im.id)}
                alt=""
                loading="lazy"
                className="h-24 w-auto rounded border border-slate-200 bg-white"
              />
              {/* Measurements on it, as the model read them — the same corner as Phase 1's. */}
              {im.dimensioned === true && <DimensionBadge />}
            </button>
            <DbToggle
              added={!pendingRemoval.has(im.id)}
              onToggle={() => onTogglePending(im.id)}
            />
          </div>
        ))}
      </div>
    </td>
  );
}


// The "in the database" switch under each catalog photo — the same pill as the Phase 1
// gallery. Filled (green) while the photo is kept; click it and it turns grey, marking the
// photo for removal WITHOUT deleting it yet — the picture stays on screen and the delete
// only happens when the user leaves the view (see the unmount cleanup above). Clicking a
// grey pill again keeps the photo, so the whole thing is undoable until you navigate away.
function DbToggle({ added, onToggle }: { added: boolean; onToggle: () => void }) {
  const { t } = useLanguage();
  return (
    <button
      type="button"
      onClick={onToggle}
      role="switch"
      aria-checked={added}
      title={t(added ? 'notice.step3.addedToDb' : 'notice.step3.addToDb')}
      className={`inline-flex max-w-[7rem] items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium transition-colors ${
        added
          ? 'bg-blue-600 text-white hover:bg-blue-500'
          : 'border border-slate-300 text-slate-500 hover:border-blue-400 hover:text-blue-600'
      }`}
    >
      <Database className="h-3 w-3 shrink-0" />
      <span className="truncate">{t(added ? 'notice.step3.addedToDb' : 'notice.step3.addToDb')}</span>
    </button>
  );
}
